import { useCallback, useState } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import useDebounce from '@/components/hooks/useDebounce';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '@/components/ui/combobox';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { toast } from '@/components/ui/toast';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useAccessToken } from '@/services/auth';
import {
  OnFarmImplantRequestError,
  type OnFarmImplantShipmentContents,
  onFarmImplantShipmentContentsOptions,
  onFarmImplantShipmentsOptions,
  type OnFarmImplantShipmentSourceType,
  type OnFarmImplantShipmentSummary,
} from '@/services/on-farm-implant';

import { shipmentCoverage } from '../../utils/shipmentSelection';

const CONTENTS_STALE_TIME = 60_000;

type ShipmentPickerProps = {
  sourceType: OnFarmImplantShipmentSourceType;
  selectedRecordKeys: ReadonlySet<string>;
  embryoPickKeys: ReadonlySet<string>;
  disabled?: boolean;
  onAdd: (
    summary: OnFarmImplantShipmentSummary,
    contents: OnFarmImplantShipmentContents,
  ) => void;
};

const shipmentItemValue = (summary: OnFarmImplantShipmentSummary) =>
  `${summary.sourceType}-${summary.shipmentId}`;

type ShipmentOptionProps = {
  summary: OnFarmImplantShipmentSummary;
  selectedRecordKeys: ReadonlySet<string>;
  embryoPickKeys: ReadonlySet<string>;
  disabled: boolean;
};

const ShipmentOption = ({
  summary,
  selectedRecordKeys,
  embryoPickKeys,
  disabled,
}: ShipmentOptionProps) => {
  const { t } = useTranslation();
  const token = useAccessToken();
  const { data: contents } = useQuery({
    ...onFarmImplantShipmentContentsOptions(token, {
      sourceType: summary.sourceType,
      shipmentId: summary.shipmentId,
    }),
    enabled: false,
  });
  const coverage = contents
    ? shipmentCoverage(contents, selectedRecordKeys, embryoPickKeys)
    : null;
  const allSelected =
    !!coverage && coverage.total > 0 && coverage.selected >= coverage.total;

  return (
    <ComboboxItem
      value={summary}
      disabled={disabled || allSelected}
      className="py-1.5"
    >
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="font-medium">{summary.shipmentNumber}</span>
        <span className="text-xs text-gray-500">
          {t('onFarmImplant.sourceCard.shipments.optionSummary', {
            records: summary.totalRecords,
            embryos: summary.totalEmbryos,
          })}
          {coverage && coverage.selected > 0 && (
            <span className="text-blue-700">
              {' · '}
              {allSelected
                ? t('onFarmImplant.sourceCard.shipments.allSelected')
                : t('onFarmImplant.sourceCard.shipments.alreadySelected', {
                    selected: coverage.selected,
                    total: coverage.total,
                  })}
            </span>
          )}
        </span>
      </div>
    </ComboboxItem>
  );
};

export function ShipmentPicker({
  sourceType,
  selectedRecordKeys,
  embryoPickKeys,
  disabled = false,
  onAdd,
}: ShipmentPickerProps) {
  const { t } = useTranslation();
  const token = useAccessToken();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const [searchText, setSearchText] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const debouncedSearch = useDebounce(searchText.trim(), 300);

  const { data: suggestions = [], isFetching } = useQuery({
    ...onFarmImplantShipmentsOptions(token, {
      sourceType,
      search: debouncedSearch,
    }),
    enabled: !!token && !disabled && debouncedSearch.length > 0,
  });

  const contentsOptions = useCallback(
    (summary: OnFarmImplantShipmentSummary) => ({
      ...onFarmImplantShipmentContentsOptions(token, {
        sourceType: summary.sourceType,
        shipmentId: summary.shipmentId,
      }),
      staleTime: CONTENTS_STALE_TIME,
    }),
    [token],
  );

  const handleHighlight = useCallback(
    (summary: OnFarmImplantShipmentSummary | undefined) => {
      if (!summary) return;
      void queryClient.prefetchQuery(contentsOptions(summary));
    },
    [contentsOptions, queryClient],
  );

  const handlePick = useCallback(
    async (summary: OnFarmImplantShipmentSummary | null) => {
      if (!summary) return;
      setIsAdding(true);
      try {
        const contents = await queryClient.fetchQuery(contentsOptions(summary));
        const coverage = shipmentCoverage(
          contents,
          selectedRecordKeys,
          embryoPickKeys,
        );
        confirm({
          title: t('onFarmImplant.sourceCard.shipments.confirmTitle', {
            shipment: summary.shipmentNumber,
          }),
          description: t(
            contents.sourceType === 'frozen'
              ? 'onFarmImplant.sourceCard.shipments.confirmBodyFrozen'
              : 'onFarmImplant.sourceCard.shipments.confirmBodyFresh',
            {
              added: coverage.total - coverage.selected,
              already: coverage.selected,
            },
          ),
          confirmLabel: t('onFarmImplant.sourceCard.shipments.confirmAdd'),
          onConfirm: () => {
            onAdd(summary, contents);
            setSearchText('');
          },
        });
      } catch (error) {
        const detail =
          error instanceof OnFarmImplantRequestError && error.problems.length
            ? error.problems.join(' ')
            : undefined;
        toast.add({
          data: { variant: 'error' },
          title: t('onFarmImplant.sourceCard.shipments.loadFailed', {
            shipment: summary.shipmentNumber,
          }),
          description: detail,
        });
      } finally {
        setIsAdding(false);
      }
    },
    [
      confirm,
      contentsOptions,
      embryoPickKeys,
      onAdd,
      queryClient,
      selectedRecordKeys,
      t,
    ],
  );

  const emptyMessage =
    debouncedSearch.length === 0
      ? t('onFarmImplant.sourceCard.shipments.typeToSearch')
      : isFetching
        ? t('onFarmImplant.sourceCard.shipments.searching')
        : t('onFarmImplant.sourceCard.shipments.noResults');

  return (
    <div className="flex items-center gap-1" data-testid="shipment-picker">
      <Combobox<OnFarmImplantShipmentSummary>
        items={debouncedSearch.length > 0 ? suggestions : []}
        filter={null}
        value={null}
        onValueChange={(summary) => void handlePick(summary)}
        inputValue={searchText}
        onInputValueChange={setSearchText}
        onItemHighlighted={handleHighlight}
        itemToStringLabel={(summary) => summary.shipmentNumber}
        itemToStringValue={shipmentItemValue}
        disabled={disabled}
      >
        <ComboboxInput
          aria-label={t('onFarmImplant.sourceCard.shipments.addLabel')}
          placeholder={t('onFarmImplant.sourceCard.shipments.addPlaceholder')}
          className="h-7 w-48 text-xs"
          disabled={disabled || isAdding}
          showTrigger={false}
        />
        <ComboboxContent className="min-w-64">
          <ComboboxEmpty>{emptyMessage}</ComboboxEmpty>
          <ComboboxList>
            {(summary: OnFarmImplantShipmentSummary) => (
              <ShipmentOption
                key={shipmentItemValue(summary)}
                summary={summary}
                selectedRecordKeys={selectedRecordKeys}
                embryoPickKeys={embryoPickKeys}
                disabled={isAdding}
              />
            )}
          </ComboboxList>
        </ComboboxContent>
      </Combobox>
      {isAdding ? (
        <i className="pi pi-spin pi-spinner text-xs text-gray-400" />
      ) : (
        <Tooltip>
          <TooltipTrigger
            render={
              <span
                className="inline-flex cursor-help text-gray-400"
                aria-label={t('onFarmImplant.sourceCard.shipments.helpLabel')}
              />
            }
          >
            <i className="pi pi-info-circle text-xs" />
          </TooltipTrigger>
          <TooltipContent>
            {t(
              sourceType === 'fresh'
                ? 'onFarmImplant.sourceCard.shipments.helpFresh'
                : 'onFarmImplant.sourceCard.shipments.helpFrozen',
            )}
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}
